import React from "react";
import { Box, TextField, Typography } from "@mui/material";
import { formFieldStyles } from "../../../styles/formFieldStyles";
import { colors, floatingLabelMaskSx } from "../../../styles/theme";
import InfoTooltip from "../overlays/InfoTooltip";

interface InputFieldMultilineProps {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  /** Beim Verlassen des Feldes – für Blur-Speichern (siehe docs/adr/004_autosave_dirty_check.md) */
  onBlur?: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  labelBackgroundColor?: string;
  error?: string;
  /** Erklärt das Feld bei Hover übers Label: was es ist und welche Auswirkung es hat */
  tooltip?: React.ReactNode;
  /** Sichtbare Zeilen im Ruhezustand; das Feld wächst mit dem Inhalt (Standard: 3) */
  minRows?: number;
  /** Obergrenze, ab der das Feld scrollt statt weiter zu wachsen */
  maxRows?: number;
  sx?: any;
}

/**
 * Mehrzeiliges Textfeld für Fließtext ohne Formatierung.
 *
 * Die Lücke zwischen `InputField` (einzeilig) und `TextEditorField` (Rich-Text mit TipTap):
 * Aufzählungen und kurze Erläuterungen, die mehr als eine Zeile brauchen, aber keine
 * Formatierung – etwa die Ausstattungsmerkmale eines Anlagentyps im Genehmigungsantrag.
 *
 * Aussehen und Verhalten sind bewusst von `InputField` übernommen, damit die Felder
 * nebeneinander gleich aussehen.
 */
const InputFieldMultiline: React.FC<InputFieldMultilineProps> = ({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  readOnly = false,
  fullWidth = true,
  required = false,
  labelBackgroundColor = colors.background.default,
  error,
  tooltip,
  minRows = 3,
  maxRows,
  sx = {},
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange && !readOnly) {
      onChange(event.target.value);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur && !readOnly) {
      onBlur(event.target.value);
    }
  };

  const labelNode = tooltip ? (
    <InfoTooltip title={tooltip} placement="top">
      <span>{label}</span>
    </InfoTooltip>
  ) : (
    label
  );

  return (
    <Box sx={{ ...formFieldStyles.container, mb: 0, ...sx }}>
      <TextField
        fullWidth={fullWidth}
        variant="outlined"
        label={labelNode}
        required={required}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        multiline
        minRows={minRows}
        maxRows={maxRows}
        error={!!error}
        size="small"
        slotProps={{ input: { readOnly } }}
        sx={{
          "& .MuiOutlinedInput-root": {
            // Einzige Abweichung von der Feldgeometrie aus dem Theme: Die dort gesetzte feste
            // Höhe und die vertikale Zentrierung gelten für einzeilige Felder. Ein mehrzeiliges
            // Feld muss mit seinem Inhalt wachsen, sonst schneidet die Höhe den Text ab.
            height: "auto",
            alignItems: "flex-start",
            "&:hover fieldset": {
              borderColor: error
                ? colors.status.error.main
                : colors.border.focus,
            },
            "&.Mui-focused fieldset": {
              borderColor: error
                ? colors.status.error.main
                : colors.border.focus,
              borderWidth: 2,
            },
            "&.Mui-focused": {
              boxShadow: `0 4px 12px ${error ? colors.shadow.error : colors.shadow.focus}`,
            },
          },
          ...floatingLabelMaskSx(labelBackgroundColor),
        }}
      />
      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ ml: 1.5, mt: 0.5, display: "block" }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default InputFieldMultiline;
